import type { Enrollment, MembershipItem } from '@spike/membership-model';
import {
    computeMembershipDisplayPrice,
    computeMembershipPreviewDiscount,
    getMembershipRemainingUses,
    isEnrollmentAvailableOnDate,
    isHourlyDaycareVariableDuration,
    resolveDaycareCatalogPrice,
    resolveDaycareSlotLinePrice,
    resolveMembershipBasePrice,
    shouldApplyMembershipToAllAppointments
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Daycare, DataVaraible, Feeding, Medication, PetServiceWrapper } from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { getDaycareDateSlotsForSave } from 'utils/daycareDateSlots';

const enrollmentFromDaycare = (daycare: Daycare): Enrollment | undefined => daycare.enrollment;

const membershipItemFromDaycare = (daycare: Daycare): MembershipItem | undefined => daycare.membershipItem;

export const buildDaycareVariableDataPerSlot = (
    daycare: Daycare,
    feedings: Array<Feeding>,
    medications: Array<Medication>,
    fallbackDate: Moment,
    timezone: string,
    isEditMode = false,
    membershipUsesAlreadyAllocated = 0,
    catalogPriceOverride?: number
): Array<DataVaraible> => {
    const durationInMinutes = moment(daycare?.to).diff(moment(daycare?.from), 'minutes');
    const daycareEnrollment = enrollmentFromDaycare(daycare);
    const membershipItem = membershipItemFromDaycare(daycare);
    const catalogPrice = resolveDaycareCatalogPrice(daycare, catalogPriceOverride);
    const slotLinePrice = resolveDaycareSlotLinePrice(daycare, catalogPrice, durationInMinutes);
    const enteredPrice = Number(daycare.price);
    const enteredPriceIsZero = Number.isFinite(enteredPrice) && enteredPrice === 0;
    const unitMembershipBase = enteredPriceIsZero ? 0 : resolveMembershipBasePrice(catalogPrice, daycare.price);
    const membershipBase = enteredPriceIsZero
        ? 0
        : isHourlyDaycareVariableDuration(daycare.service?.variableDuration)
        ? unitMembershipBase
        : resolveMembershipBasePrice(catalogPrice, slotLinePrice);

    const baseDaycareService: PetServiceWrapper = {
        service: {
            id: daycare.service?.serviceId || 0,
            name: daycare.service?.serviceName || 'Daycare Service'
        },
        price: slotLinePrice,
        beforePrice: catalogPrice > 0 ? catalogPrice : daycare.service?.beforePrice ?? daycare.price,
        durationInMinutes: durationInMinutes || 0,
        duration: {
            hours: durationInMinutes ? Math.trunc(durationInMinutes / 60) : 0,
            minutes: durationInMinutes ? durationInMinutes % 60 : 0
        },
        discount: {
            type: daycare.discount?.type || 'amount',
            value: daycare.discount?.value ?? 0
        },
        room: daycare.room,
        roomUnitId: daycare.roomUnitId,
        feedings,
        medications,
        businessArea: 'daycare',
        color: daycare.service?.serviceColor ?? '',
        latePickupFee: daycare.service?.latePickupFee,
        variableDuration: daycare.service?.variableDuration,
        saveCustomPrice: daycare.saveCustomPrice,
        customPriceId: daycare.customPriceId,
        customPriceAmount: unitMembershipBase > 0 ? unitMembershipBase : daycare.price
    };

    const daycareSlots = getDaycareDateSlotsForSave(daycare, fallbackDate, timezone, isEditMode);
    const applyMembershipToAll =
        daycareEnrollment != null &&
        membershipItem != null &&
        shouldApplyMembershipToAllAppointments(membershipItem, daycareEnrollment);
    let limitedMembershipUsesRemaining =
        daycareEnrollment != null && membershipItem != null && !applyMembershipToAll
            ? Math.max(
                  0,
                  getMembershipRemainingUses(membershipItem, daycareEnrollment) - membershipUsesAlreadyAllocated
              )
            : Number.POSITIVE_INFINITY;

    return daycareSlots.map(slot => {
        const membershipAvailableOnDate =
            daycareEnrollment != null &&
            membershipItem != null &&
            isEnrollmentAvailableOnDate(daycareEnrollment, slot.date, timezone);
        let applyMembership = false;

        if (membershipAvailableOnDate) {
            if (applyMembershipToAll) {
                applyMembership = true;
            } else if (limitedMembershipUsesRemaining > 0) {
                applyMembership = true;
                limitedMembershipUsesRemaining -= 1;
            }
        }

        const serviceForSlot: PetServiceWrapper = applyMembership
            ? (() => {
                  const membershipPricing = computeMembershipDisplayPrice(membershipBase, membershipItem);
                  const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                  return {
                      ...baseDaycareService,
                      price: membershipPricing.displayPrice,
                      beforePrice: unitMembershipBase > 0 ? unitMembershipBase : catalogPrice,
                      customPriceAmount: unitMembershipBase > 0 ? unitMembershipBase : daycare.price,
                      discount: previewDiscount
                          ? { type: previewDiscount.type, value: previewDiscount.value }
                          : { type: 'amount' as const, value: 0 },
                      enrollment: daycareEnrollment,
                      membershipItem
                  };
              })()
            : {
                  ...baseDaycareService,
                  price: slotLinePrice,
                  beforePrice: catalogPrice > 0 ? catalogPrice : baseDaycareService.beforePrice,
                  discount: { type: 'amount', value: 0 },
                  enrollment: undefined,
                  membershipItem: undefined
              };

        const discountForSlot =
            daycareEnrollment != null && membershipItem != null && !applyMembership
                ? { type: 'amount' as const, value: 0 }
                : applyMembership
                ? (() => {
                      const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                      return previewDiscount
                          ? {
                                type: previewDiscount.type as 'amount' | 'percentage',
                                value: previewDiscount.value
                            }
                          : { type: 'percentage' as const, value: 100 };
                  })()
                : {
                      type: (daycare.discount?.type || 'amount') as 'amount' | 'percentage',
                      value: daycare.discount?.value ?? 0
                  };

        return {
            service: serviceForSlot,
            staffId: daycare.room?.staffIds?.[0],
            date: slot.date,
            dateEnd: slot.dateEnd,
            itemData: {
                discount: discountForSlot,
                flow: 'daycare',
                price: enteredPriceIsZero ? 0 : membershipBase || catalogPrice
            }
        };
    });
};
